// eslint-disable-next-line max-len
/* eslint-disable @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call */

import { Logger } from '@feature-hub/core';
import {
  cleanupEmissionConsumptionMarkup,
  FootnoteReferenceV2,
  getConsumptionsAndEmissions,
  renderTextWithFootnotesReferencesV2,
} from '@oneaudi/feature-app-utils';
import { VueFormatterServiceInterfaceV1 } from '@oneaudi/vue-formatter-service';
import { GfaLocaleServiceV1 } from '@volkswagen-onehub/gfa-locale-service';
/* eslint-disable react/display-name */
import { createElement, ReactNode } from 'react';
import { Content } from '../EditorContentTypes';
import { InitialState, ThemeType, WltpProps } from '../FeatureAppTypes';
import { darkTheme, lightTheme } from '../theme';

export async function createInitialState(
  content: Content,
  vueFormatterService: VueFormatterServiceInterfaceV1,
  localeService: GfaLocaleServiceV1,
  logger?: Logger,
): Promise<InitialState> {
  const consumptionsAndEmissionsValues = await getConsumptionsAndEmissions(
    content.wltp,
    vueFormatterService,
    localeService,
    logger,
  );
  const headlines: Iterable<ReactNode>[] = [];

  if (content.animation.headlines) {
    content.animation.headlines.map((headline) =>
      headlines.push(renderTextWithFootnotesReferencesV2(headline)),
    );
  }

  let additionalText: Iterable<ReactNode> | undefined;
  if (content.additionalText) {
    additionalText = renderTextWithFootnotesReferencesV2(content.additionalText);
  }

  const wltp: WltpProps[] = [];
  consumptionsAndEmissionsValues.forEach(
    ({
      formattedConsumption,
      formattedEmission,
      formattedCo2Class,
      formattedDischargedCo2Class,
      formattedDischargedConsumption,
    }) => {
      const wltpData: WltpProps = {};

      if (formattedConsumption && formattedEmission) {
        wltpData.formattedConsumption = renderTextWithFootnotesReferencesV2(
          cleanupEmissionConsumptionMarkup(formattedConsumption),
        );
        wltpData.formattedEmission = renderTextWithFootnotesReferencesV2(
          cleanupEmissionConsumptionMarkup(formattedEmission),
        );

        if (formattedCo2Class) {
          wltpData.formattedCo2Class = renderTextWithFootnotesReferencesV2(
            cleanupEmissionConsumptionMarkup(formattedCo2Class),
          );
        }

        if (formattedDischargedCo2Class) {
          wltpData.formattedDischargedCo2Class = renderTextWithFootnotesReferencesV2(
            cleanupEmissionConsumptionMarkup(formattedDischargedCo2Class),
          );
        }

        if (formattedDischargedConsumption) {
          wltpData.formattedDischargedConsumption = renderTextWithFootnotesReferencesV2(
            cleanupEmissionConsumptionMarkup(formattedDischargedConsumption),
          );
        }

        if (Object.keys(wltpData).length > 0) {
          wltp.push(wltpData);
        }
      }
    },
  );

  const theme = content.themeScroll === ThemeType.dark ? darkTheme : lightTheme;

  return {
    ...content,
    themeScroll: theme,
    animation: {
      ...content.animation,
      headlines,
    },
    additionalText,
    wltp,
  };
}

interface SerializedWltpProps {
  formattedConsumption: (string | Record<string, unknown>)[] | undefined;
  formattedEmission: (string | Record<string, unknown>)[] | undefined;
  formattedCo2Class: (string | Record<string, unknown>)[] | undefined;
  formattedDischargedConsumption: (string | Record<string, unknown>)[] | undefined;
  formattedDischargedCo2Class: (string | Record<string, unknown>)[] | undefined;
}

export const deserializeReactNodeArray = (
  deserializedProperty?: string | (string | Record<string, unknown>)[],
): undefined | string | ReactNode[] => {
  if (!deserializedProperty || typeof deserializedProperty === 'string') {
    // if it's undefined or a string it doesn't contain any footnotes. Nothing to do here
    return deserializedProperty;
  }
  return deserializedProperty.map((serializedReactNode) => {
    if (typeof serializedReactNode === 'string') {
      return serializedReactNode;
    }
    // if it's not a string it has to be a <FootnoteReference /> react component
    return createElement(FootnoteReferenceV2, serializedReactNode.props as undefined);
  });
};

const deserializeReactNodeArrayInWltpData = (wltpData: SerializedWltpProps[]) => {
  return wltpData.map(
    ({
      formattedConsumption,
      formattedEmission,
      formattedCo2Class,
      formattedDischargedConsumption,
      formattedDischargedCo2Class,
    }) => {
      return {
        formattedConsumption: deserializeReactNodeArray(formattedConsumption),
        formattedEmission: deserializeReactNodeArray(formattedEmission),
        formattedCo2Class: deserializeReactNodeArray(formattedCo2Class),
        formattedDischargedConsumption: deserializeReactNodeArray(formattedDischargedConsumption),
        formattedDischargedCo2Class: deserializeReactNodeArray(formattedDischargedCo2Class),
      };
    },
  );
};

/**
 * Helper function to deserialize the state of the feature app. It converts
 * serialized ReactNodeArray Entries that aren't string to FootnoteReference components
 * @param state
 */
export function deserializeState(state: string): InitialState {
  const props = JSON.parse(state);

  let headlinesArray: (string | ReactNode[])[] = [];
  if (props && props.animation && Array.isArray(props.animation.headlines)) {
    headlinesArray = props.animation.headlines.map((headline: string[]) => {
      return deserializeReactNodeArray(headline);
    });
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return {
    ...props,
    additionalText: deserializeReactNodeArray(props.additionalText),
    animation: {
      ...props.animation,
      headlines: headlinesArray,
    },
    wltp: deserializeReactNodeArrayInWltpData(props.wltp),
  };
}
